import React, { useEffect, useMemo, useRef, useState } from 'react';
import MoreMenu, { COLORS, colorName, swatch } from './MoreMenu';
import useReorder from './useReorder';
import { Icon } from './icons';
import {
  DATE_OPS, DATE_VALUES, DATE_WORDS, DURATION_OPS, FIELDS, blankCond, blankGroup, depthOf, fromModel, modelText,
  print, suggestionsFor, toModel
} from '../../lib/todoFilterQuery';
import './TodoFilters.css';

/**
 * Saved filters (migration 074): the Filters page, and the editor a filter is
 * made and changed in.
 *
 * Gordon: Todoist's filters, with "a friendly builder UI so nobody has to
 * type syntax", and the query itself for whoever would rather type it.
 *
 * THE PAGE lists the person's filters in their order - dragged by the grip,
 * or Move up / Move down from the ⋯ for the keyboard - each with its colour,
 * its icon, its query, how many to-dos it shows now, and a pin that puts it
 * in the sidebar. Deleting says so with an Undo. An empty page offers a few
 * filters to start from, one click each (only those this server can
 * answer: no "Long tasks today" before 073 has given to-dos a duration).
 *
 * THE EDITOR has two faces of one query. BUILDER: rows of conditions under
 * "All of" / "Any of" (and "None of", "Not all of"), groups inside groups
 * three deep. QUERY: the text, as the server reads it. Changing the builder
 * rewrites the text at once (lib/todoFilterQuery print); changing the text
 * is read by the server a moment later, and its tree is what the builder
 * shows when it is switched back to - so the two can never disagree, because
 * there is only one parser. A text the server cannot read says why, and
 * where: the very characters are marked. All the while, the count of what it
 * matches, and the first few of them.
 */

const MODE_WORDS = [['all', false, 'All of'], ['any', false, 'Any of'], ['any', true, 'None of'], ['all', true, 'Not all of']];
const ICONS = ['filter', 'flag', 'today', 'upcoming', 'person', 'team', 'briefcase', 'clock', 'calendar',
  'tag', 'receipt', 'audit', 'shield', 'bell', 'repeat', 'ticket'];
const ICON_NAMES = { filter: 'Filter', flag: 'Flag', today: 'Today', upcoming: 'Upcoming', person: 'Person', team: 'Team',
  briefcase: 'Briefcase', clock: 'Clock', calendar: 'Calendar', tag: 'Tag', receipt: 'Receipt', audit: 'Audit',
  shield: 'Shield', bell: 'Bell', repeat: 'Repeat', ticket: 'Ticket' };

const plural = (k, one) => `${k} ${one}${k === 1 ? '' : 's'}`;

/** A filter's mark: its icon on its colour. */
export const FilterMark = ({ filter, size = 14 }) => (
  <span className={`tdf-mark ${swatch(filter?.color)}`} aria-hidden="true">
    <Icon name={filter?.icon || 'filter'} size={size} />
  </span>
);

/* ===================================================================== */
/*                               the page                                */
/* ===================================================================== */

export default function TodoFilters({ smart, ctx, openById, phone, busy = false, showToast, onOpen, editing, setEditing }) {
  const reorder = useReorder((group, from, to) => { if (group === 'filters') smart.reorder(from, to); });
  const filters = smart.filters || [];
  const [problem, setProblem] = useState(null);

  const save = async (values) => {
    setProblem(null);
    if (editing && editing.id) {
      await smart.update(editing, values);
      setEditing(null);
      showToast(`Saved “${values.name}”`);
      return null;
    }
    const made = await smart.create(values);
    setEditing(null);
    showToast(`Made “${made?.name || values.name}”`);
    return made;
  };

  const duplicate = async (f) => {
    try {
      const made = await smart.create({ name: `${f.name} (copy)`.slice(0, 80), query: f.query, color: f.color, icon: f.icon,
                                        pinned: false });
      showToast(`Made a copy of “${f.name}”`);
      return made;
    } catch (err) {
      setProblem(err.response?.data?.error || 'Could not copy that filter');
      return null;
    }
  };

  const remove = async (f) => {
    setProblem(null);
    try {
      await smart.remove(f);
      showToast(`Deleted “${f.name}”`, { undo: () => smart.restore(f).catch(() => setProblem('Could not bring it back')) });
    } catch (err) {
      setProblem(err.response?.data?.error || 'Could not delete that filter');
    }
  };

  if (editing) {
    return (
      <div className="tdf-page">
        <FilterEditor key={editing.id || editing.key || 'new'} initial={editing} smart={smart} ctx={ctx} openById={openById}
                      phone={phone} onSave={save} onCancel={() => setEditing(null)} />
      </div>
    );
  }

  const suggestions = suggestionsFor(smart.fields);
  return (
    <div className="tdf-page">
      <div className="tdf-page-head">
        <p className="tdf-lede">
          A filter is a saved question - “P1 due this week”, “waiting on someone”. Pin one to see it in the sidebar with its count.
        </p>
        <button type="button" className="btn btn-primary btn-small tdf-new" disabled={busy}
                onClick={() => setEditing({ key: 'new' })}>
          <Icon name="plus" size={14} />New filter
        </button>
      </div>
      {problem && <p className="ctd-error" role="alert">{problem}</p>}

      {smart.filters === null ? <p className="ctd-quiet">Loading…</p> : filters.length === 0 ? (
        <div className="tdf-empty" role="region" aria-label="Start from a suggestion">
          <span className="ctd-empty-ic" aria-hidden="true"><Icon name="filter" size={28} /></span>
          <p className="ctd-empty-title">No filters yet</p>
          <p className="ctd-empty-text">Start from one of these, or make your own.</p>
          <ul className="tdf-suggest">
            {suggestions.map((s) => (
              <li key={s.key}>
                <button type="button" className="tdf-suggest-card" data-suggestion={s.key} disabled={busy}
                        onClick={async () => {
                          try { await smart.create({ name: s.name, query: s.query, color: s.color, icon: s.icon, pinned: true }); showToast(`Added “${s.name}” to the sidebar`); } catch (err) {
                            setProblem(err.response?.data?.error || 'Could not add that filter');
                          }
                        }}>
                  <FilterMark filter={s} size={16} />
                  <span className="tdf-suggest-text">
                    <span className="tdf-suggest-name">{s.name}</span>
                    <span className="tdf-suggest-why">{s.text}</span>
                    <code className="tdf-q">{s.query}</code>
                  </span>
                  <span className="tdf-suggest-add" aria-hidden="true"><Icon name="plus" size={14} /></span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <ul className="tdf-list" aria-label="Your filters" data-reorder="filters">
          {filters.map((f, i) => (
            <li key={f.id} className={`tdf-row${reorder.markOf('filters', i, filters.length)}`} data-reorder-item=""
                data-filter-row={f.id}>
              <span className="tda-grip" aria-hidden="true" title="Drag to reorder"
                    onPointerDown={(e) => reorder.start(e, 'filters', i)}><Icon name="grip" size={14} /></span>
              <FilterMark filter={f} />
              <button type="button" className="tdf-row-main" onClick={() => onOpen(f.id)}
                      aria-label={`Open “${f.name}”${f.count !== null ? `, ${plural(f.count, 'to-do')}` : ''}`}>
                <span className="tdf-row-name">{f.name}</span>
                <code className="tdf-q" title={f.query}>{f.query}</code>
                {f.error && <span className="tdf-row-err" role="note">{f.error.error}</span>}
              </button>
              <span className="tda-n tdf-row-n">{f.count ?? '–'}</span>
              <button type="button" className={`tdf-pin${f.pinned ? ' is-on' : ''}`} aria-pressed={f.pinned} disabled={busy}
                      aria-label={f.pinned ? `Unpin “${f.name}” from the sidebar` : `Pin “${f.name}” to the sidebar`}
                      title={f.pinned ? 'In the sidebar - unpin' : 'Pin to the sidebar'}
                      onClick={() => smart.update(f, { pinned: !f.pinned }).catch((err) => setProblem(err.response?.data?.error || 'Could not change that'))}>
                <Icon name="pin" size={15} />
              </button>
              <MoreMenu label={`More for filter “${f.name}”`} disabled={busy} items={[
                { label: 'Open', icon: 'filter', onClick: () => onOpen(f.id) },
                { label: 'Edit', icon: 'edit', onClick: () => setEditing(f) },
                { label: 'Duplicate', icon: 'copy', onClick: () => duplicate(f) },
                { label: f.pinned ? 'Unpin from the sidebar' : 'Pin to the sidebar', icon: 'pin',
                  onClick: () => smart.update(f, { pinned: !f.pinned }).catch(() => {}) },
                { sep: true },
                i > 0 && { label: 'Move up', icon: 'arrowUp', onClick: () => smart.reorder(i, i - 1) },
                i < filters.length - 1 && { label: 'Move down', icon: 'arrowDown', onClick: () => smart.reorder(i, i + 1) },
                { sep: true },
                { label: 'Delete', icon: 'trash', danger: true, onClick: () => remove(f) }
              ]} />
            </li>
          ))}
        </ul>
      )}
      {filters.length > 0 && suggestions.some((s) => !filters.some((f) => f.query === s.query)) && (
        <details className="tdf-more-ideas">
          <summary>Ideas for more</summary>
          <ul className="tdf-ideas">
            {suggestions.filter((s) => !filters.some((f) => f.query === s.query)).map((s) => (
              <li key={s.key}>
                <button type="button" className="tda-add-line" data-suggestion={s.key}
                        onClick={() => setEditing({ key: s.key, name: s.name, query: s.query, color: s.color, icon: s.icon, pinned: true })}>
                  + {s.name} <code className="tdf-q">{s.query}</code>
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/* ===================================================================== */
/*                              the editor                               */
/* ===================================================================== */

/**
 * `initial` is the filter being changed (with an id), a suggestion, or
 * { key: 'new' }. `ctx` is what the value pickers offer: { lists, labels,
 * sections, companies, people }.
 */
export function FilterEditor({ initial, smart, ctx, openById, phone, onSave, onCancel }) {
  const startText = initial.query || 'p1';
  const [name, setName] = useState(initial.name || '');
  const [color, setColor] = useState(initial.color || null);
  const [icon, setIcon] = useState(initial.icon || 'filter');
  const [pinned, setPinned] = useState(initial.pinned !== false);
  const [mode, setMode] = useState('builder');
  const [text, setText] = useState(startText);
  const [model, setModel] = useState(initial.query ? null : blankGroup('all', [blankCond('priority')]));
  const [result, setResult] = useState(null);     // { text, ok, count, ids, tree, error }
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState(null);
  const [wantBuilder, setWantBuilder] = useState(false);
  const nameRef = useRef(null);
  const textRef = useRef(null);
  const fields = smart.fields && smart.fields.length ? smart.fields : Object.keys(FIELDS);

  // The server reads the text a moment after it stops changing.
  useEffect(() => {
    const q = text.trim();
    if (!q) { setResult({ text, ok: false, error: { error: 'Write a query, or add a condition', position: 0, length: 1 } }); return undefined; }
    let live = true;
    const t = setTimeout(() => {
      smart.preview(text)
        .then((r) => { if (live) setResult({ text, ok: true, count: r.data.count, ids: r.data.ids || [], tree: r.data.tree }); })
        .catch((err) => {
          if (!live) return;
          const b = err.response?.data || {};
          setResult({ text, ok: false, error: { error: b.error || 'Could not try that filter', position: b.position, length: b.length } });
        });
    }, result === null ? 0 : 300);
    return () => { live = false; clearTimeout(t); };
    // The preview is keyed on the text alone.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const current = result && result.text === text ? result : null;

  // An existing filter opens in the builder once its tree is known; one the
  // server cannot read (any more) opens on its text, saying why.
  useEffect(() => {
    if (model || !current) return;
    if (current.ok) setModel(toModel(current.tree));
    else setMode('text');
  }, [model, current]);

  // Back to the builder from the text, once the server has read it.
  useEffect(() => {
    if (!wantBuilder || !current) return;
    setWantBuilder(false);
    if (current.ok) { setModel(toModel(current.tree)); setMode('builder'); }
  }, [wantBuilder, current]);

  const changeModel = (next) => {
    setModel(next);
    setText(modelText(next));
  };

  const toBuilder = () => {
    if (mode === 'builder') return;
    if (current && current.ok) { setModel(toModel(current.tree)); setMode('builder'); return; }
    if (current && !current.ok) return;   // the error says what to fix
    setWantBuilder(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setProblem(null);
    const n = name.trim();
    if (!n) { setProblem('Give the filter a name'); if (nameRef.current) nameRef.current.focus(); return; }
    const query = mode === 'builder' && model ? (print(fromModel(model)) || text) : text;
    if (!query.trim()) { setProblem('Add a condition first'); return; }
    setSaving(true);
    try {
      await onSave({ name: n, query: query.trim(), color, icon, pinned });
    } catch (err) {
      const b = err.response?.data || {};
      setProblem(b.error || 'Could not save that filter');
      if (b.position !== undefined) { setMode('text'); setResult({ text: query, ok: false, error: b }); }
    } finally { setSaving(false); }
  };

  const samples = current && current.ok ? current.ids.map((id) => openById.get(String(id))).filter(Boolean).slice(0, 5) : [];
  const err = current && !current.ok ? current.error : null;
  const tooDeep = model && depthOf(model) > 3;

  return (
    <form className="tdf-editor" onSubmit={submit} aria-label={initial.id ? `Edit filter “${initial.name}”` : 'New filter'}
          onKeyDown={(e) => { if (e.key === 'Escape' && !e.defaultPrevented) { e.preventDefault(); onCancel(); } }}>
      <div className="tdf-ident">
        <label className="tdf-field tdf-name">
          <span>Name</span>
          <input ref={nameRef} type="text" value={name} maxLength={80} autoFocus={!initial.id && !phone}
                 placeholder="P1 due this week" onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="tdf-field">
          <span>Colour</span>
          <select value={color || ''} onChange={(e) => setColor(e.target.value || null)}>
            <option value="">No colour</option>
            {COLORS.map((c) => <option key={c} value={c}>{colorName(c)}</option>)}
          </select>
        </label>
        <label className="tdf-field">
          <span>Icon</span>
          <span className="tdf-icon-pick">
            <FilterMark filter={{ color, icon }} />
            <select value={icon} onChange={(e) => setIcon(e.target.value)}>
              {ICONS.map((k) => <option key={k} value={k}>{ICON_NAMES[k]}</option>)}
            </select>
          </span>
        </label>
        <label className="tdf-check">
          <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} />
          <span>Show in the sidebar</span>
        </label>
      </div>

      <div className="tdf-tabs" role="tablist" aria-label="Edit the filter as">
        <button type="button" role="tab" aria-selected={mode === 'builder'} className="tdf-tab"
                onClick={toBuilder} disabled={mode === 'text' && Boolean(err)}
                title={mode === 'text' && err ? 'Fix the query first' : undefined}>Builder</button>
        <button type="button" role="tab" aria-selected={mode === 'text'} className="tdf-tab"
                onClick={() => { setMode('text'); requestAnimationFrame(() => textRef.current && textRef.current.focus()); }}>
          Query
        </button>
      </div>

      {mode === 'builder' ? (
        <div className="tdf-builder" role="tabpanel" aria-label="Builder">
          {!model ? <p className="ctd-quiet">Reading the filter…</p> : (
            <>
              {tooDeep && <p className="tdf-hint">This query nests deeper than the builder draws comfortably - the Query tab shows all of it.</p>}
              <Group group={model} top fields={fields} ctx={ctx} depth={1}
                     onChange={changeModel} />
            </>
          )}
          {model && <p className="tdf-as-text">As a query: <code className="tdf-q">{modelText(model) || '…'}</code></p>}
        </div>
      ) : (
        <div className="tdf-text" role="tabpanel" aria-label="Query">
          <label className="tdf-field tdf-query">
            <span>Query</span>
            <input ref={textRef} type="text" value={text} spellCheck={false} autoComplete="off" maxLength={1000}
                   aria-invalid={Boolean(err)} aria-describedby={err ? 'tdf-q-err' : 'tdf-q-help'}
                   onChange={(e) => setText(e.target.value)} />
          </label>
          {err && (
            <div className="tdf-error" id="tdf-q-err" role="alert">
              <p>{err.error}</p>
              {Number.isInteger(err.position) && text && (
                <code className="tdf-mirror" aria-hidden="true">
                  {text.slice(0, err.position)}
                  <mark>{text.slice(err.position, err.position + (err.length || 1)) || ' '}</mark>
                  {text.slice(err.position + (err.length || 1))}
                </code>
              )}
            </div>
          )}
          <details className="tdf-help" id="tdf-q-help">
            <summary>How to write a query</summary>
            <dl>
              <dt><code>&amp;</code> <code>|</code> <code>!</code> <code>( )</code></dt><dd>and, or, not, and brackets - <code>p1 &amp; (today | overdue)</code></dd>
              <dt><code>p1</code> … <code>p4</code>, <code>priority:&lt;=2</code></dt><dd>priority</dd>
              <dt><code>@VAT</code> <code>#Inbox</code> <code>#"Year end"</code></dt><dd>a label, a list (quotes round names with spaces)</dd>
              <dt><code>today</code> <code>overdue</code> <code>due:this_week</code> <code>due:before:2026-11-01</code> <code>no date</code></dt><dd>dates - also <code>deadline:</code></dd>
              <dt><code>assignee:me</code> <code>unassigned</code> <code>created_by:me</code></dt><dd>people</dd>
              <dt><code>flagged</code> <code>waiting</code> <code>repeating</code> <code>shared</code></dt><dd>yes-or-no ones</dd>
              <dt><code>duration:&gt;=2h</code> <code>client:ALPH</code> <code>search:"bank rec"</code></dt><dd>and the rest</dd>
            </dl>
          </details>
        </div>
      )}

      <div className="tdf-preview" aria-live="polite" data-preview="">
        {!current ? <span className="ctd-quiet">Counting…</span>
          : current.ok ? (
            <>
              <strong data-preview-count={current.count}>{current.count === 0 ? 'Nothing matches yet' : `${plural(current.count, 'to-do')} match`}</strong>
              {samples.length > 0 && (
                <ul className="tdf-samples">
                  {samples.map((t) => <li key={t.id}>{t.title}</li>)}
                  {current.count > samples.length && <li className="tdf-more">and {current.count - samples.length} more</li>}
                </ul>
              )}
            </>
          ) : <span className="tdf-preview-bad">The query needs fixing before it can be counted.</span>}
      </div>

      {problem && <p className="ctd-error" role="alert">{problem}</p>}
      <div className="tdf-actions">
        <button type="submit" className="btn btn-primary" disabled={saving || Boolean(err)}>
          {initial.id ? 'Save' : 'Save filter'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

/* --------------------------------------------------------- a group */

function Group({ group, top = false, fields, ctx, depth, onChange, onRemove = null }) {
  const modeKey = `${group.mode}:${group.negate ? 'not' : ''}`;
  const set = (patch) => onChange({ ...group, ...patch });
  const setItem = (i, next) => set({ items: group.items.map((x, j) => (j === i ? next : x)) });
  const dropItem = (i) => {
    const items = group.items.filter((_, j) => j !== i);
    set({ items });
  };
  const addCond = () => {
    const used = new Set(group.items.filter((x) => x.kind === 'cond').map((x) => x.field));
    const next = ['due', 'priority', 'label', 'list', 'flagged', 'assignee'].find((f) => fields.includes(f) && !used.has(f)) || fields[0];
    set({ items: [...group.items, blankCond(next)] });
  };
  const addGroup = () => set({ items: [...group.items, blankGroup(group.mode === 'all' ? 'any' : 'all', [blankCond('due')])] });
  return (
    <fieldset className={`tdf-group${top ? ' is-top' : ''}`} data-depth={depth}>
      <legend className="tdf-group-head">
        {top && <span>Show to-dos that match</span>}
        <select aria-label={top ? 'Match' : 'This group matches'} value={modeKey}
                onChange={(e) => {
                  const [m, neg] = e.target.value.split(':');
                  set({ mode: m, negate: neg === 'not' });
                }}>
          {MODE_WORDS.map(([m, neg, words]) => <option key={`${m}:${neg}`} value={`${m}:${neg ? 'not' : ''}`}>{words.toLowerCase()}</option>)}
        </select>
        <span>these</span>
        {onRemove && (
          <button type="button" className="tdf-x" aria-label="Remove this group" title="Remove this group" onClick={onRemove}>
            <Icon name="close" size={14} />
          </button>
        )}
      </legend>
      <ol className="tdf-rows">
        {group.items.map((x, i) => (
          <li key={x.id} className="tdf-row-item">
            {i > 0 && <span className="tdf-join" aria-hidden="true">{group.mode === 'any' ? 'or' : 'and'}</span>}
            {x.kind === 'group' ? (
              <Group group={x} fields={fields} ctx={ctx} depth={depth + 1}
                     onChange={(next) => setItem(i, next)} onRemove={() => dropItem(i)} />
            ) : (
              <Cond row={x} index={i + 1} fields={fields} ctx={ctx}
                    onChange={(next) => setItem(i, next)} onRemove={group.items.length > 1 || !top ? () => dropItem(i) : null} />
            )}
          </li>
        ))}
      </ol>
      <div className="tdf-group-add">
        <button type="button" className="tda-add-line" onClick={addCond}>+ Condition</button>
        {depth < 3 && <button type="button" className="tda-add-line" onClick={addGroup}>+ Group</button>}
      </div>
    </fieldset>
  );
}

/* ----------------------------------------------------- a condition */

function Cond({ row, index, fields, ctx, onChange, onRemove }) {
  const f = FIELDS[row.field] || FIELDS.priority;
  const set = (patch) => onChange({ ...row, ...patch });
  const say = (what) => `Condition ${index}: ${what}`;
  const offered = fields.includes(row.field) ? fields : [row.field, ...fields];
  const negatable = f.is && f.kind !== 'bool' && f.kind !== 'presence';
  return (
    <div className="tdf-cond" data-cond={row.field}>
      <select className="tdf-cond-field" aria-label={say('field')} value={row.field}
              onChange={(e) => onChange({ ...blankCond(e.target.value), id: row.id })}>
        {offered.map((k) => FIELDS[k] && <option key={k} value={k}>{FIELDS[k].label}</option>)}
      </select>
      {negatable && (
        <select className="tdf-cond-not" aria-label={say('is or is not')} value={row.negate ? 'not' : 'is'}
                onChange={(e) => set({ negate: e.target.value === 'not' })}>
          <option value="is">{f.is[0]}</option>
          <option value="not">{f.is[1]}</option>
        </select>
      )}
      <Value row={row} f={f} ctx={ctx} say={say} set={set} />
      {onRemove ? (
        <button type="button" className="tdf-x" aria-label={say('remove')} title="Remove" onClick={onRemove}>
          <Icon name="close" size={14} />
        </button>
      ) : <span className="tdf-x-gap" aria-hidden="true" />}
    </div>
  );
}

const PRIORITY_CHOICES = [['is', '1', 'P1'], ['is', '2', 'P2'], ['is', '3', 'P3'], ['is', '4', 'P4'],
  ['<=', '2', 'P2 or higher'], ['<=', '3', 'P3 or higher'], ['>=', '2', 'P2 or lower'], ['>=', '3', 'P3 or lower']];

function Value({ row, f, ctx, say, set }) {
  const names = useMemo(() => {
    switch (row.field) {
      case 'label': return (ctx.labels || []).map((b) => [b.name, b.name]);
      case 'list': return (ctx.lists || []).map((l) => [l.name, l.name]);
      case 'section': return [...new Set((ctx.lists || []).flatMap((l) => (l.sections || []).map((s) => s.name)))].map((s) => [s, s]);
      case 'client': return (ctx.companies || []).filter((c) => !c.archived).map((c) => [c.name, c.short_code ? `${c.name} (${c.short_code})` : c.name]);
      case 'assignee':
      case 'created_by': return (ctx.people || []).map((p) => [p, p]);
      default: return [];
    }
  }, [row.field, ctx]);

  switch (f.kind) {
    case 'priority': {
      const here = `${row.op}:${row.value}`;
      const choices = PRIORITY_CHOICES.some(([o, v]) => `${o}:${v}` === here) ? PRIORITY_CHOICES
        : [...PRIORITY_CHOICES, [row.op, row.value, `priority ${row.op} ${row.value}`]];
      return (
        <select aria-label={say('priority')} value={here} onChange={(e) => {
          const [op, value] = e.target.value.split(':');
          set({ op, value });
        }}>
          {choices.map(([o, v, w]) => <option key={`${o}:${v}`} value={`${o}:${v}`}>{w}</option>)}
        </select>
      );
    }
    case 'name': {
      const words = f.words || [];
      const here = row.named || !words.some(([k]) => k === row.value) ? (row.value ? `n:${row.value}` : '') : `w:${row.value}`;
      const known = names.some(([v]) => v.toLowerCase() === String(row.value).toLowerCase());
      return (
        <select aria-label={say(f.label.toLowerCase())} value={here} className={!row.value ? 'is-empty' : ''}
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) set({ value: '', named: false });
                  else if (v.startsWith('w:')) set({ value: v.slice(2), named: false });
                  else set({ value: v.slice(2), named: true });
                }}>
          {!row.value && <option value="">Choose…</option>}
          {words.map(([k, w]) => <option key={`w:${k}`} value={`w:${k}`}>{w}</option>)}
          {names.length > 0 && words.length > 0 && <option disabled value="--">──────</option>}
          {names.map(([v, w]) => <option key={`n:${v}`} value={`n:${v}`}>{w}</option>)}
          {row.value && here.startsWith('n:') && !known && <option value={here}>{row.value} (not found)</option>}
        </select>
      );
    }
    case 'date': {
      const cmp = row.op !== 'is';
      const head = cmp ? `cmp:${row.op}` : `is:${row.value}`;
      const iso = /^\d{4}-\d{2}-\d{2}$/.test(row.value);
      const valueChoices = DATE_VALUES.some(([k]) => k === row.value) || iso ? DATE_VALUES : [...DATE_VALUES, [row.value, row.value]];
      return (
        <>
          <select aria-label={say('when')} value={head} onChange={(e) => {
            const [kind, v] = e.target.value.split(':');
            if (kind === 'is') set({ op: 'is', value: v });
            else set({ op: v, value: cmp ? row.value : 'today' });
          }}>
            {DATE_WORDS.map(([k, w]) => <option key={`is:${k}`} value={`is:${k}`}>{w}</option>)}
            {DATE_OPS.map(([k, w]) => <option key={`cmp:${k}`} value={`cmp:${k}`}>{w}…</option>)}
          </select>
          {cmp && (
            <select aria-label={say('which day')} value={iso ? 'date' : row.value}
                    onChange={(e) => set({ value: e.target.value === 'date' ? (iso ? row.value : new Date().toISOString().slice(0, 10)) : e.target.value })}>
              {valueChoices.map(([k, w]) => <option key={k} value={k}>{w}</option>)}
              <option value="date">a date…</option>
            </select>
          )}
          {cmp && iso && (
            <input type="date" aria-label={say('date')} value={row.value}
                   onChange={(e) => { if (e.target.value) set({ value: e.target.value }); }} />
          )}
        </>
      );
    }
    case 'duration': return <Duration row={row} say={say} set={set} />;
    case 'bool':
      return (
        <select aria-label={say(f.label.toLowerCase())} value={row.value} onChange={(e) => set({ value: e.target.value })}>
          <option value="yes">yes</option>
          <option value="no">no</option>
        </select>
      );
    case 'presence':
      return (
        <select aria-label={say('ticket')} value={row.value} onChange={(e) => set({ value: e.target.value })}>
          <option value="any">linked to one</option>
          <option value="none">not linked</option>
        </select>
      );
    default:
      return (
        <input type="text" className="tdf-words" aria-label={say('words')} value={row.value} maxLength={120}
               placeholder="bank rec" onChange={(e) => set({ value: e.target.value })} />
      );
  }
}

/** How long: a comparison and an amount, in minutes or hours, or set / not set. */
function Duration({ row, say, set }) {
  const cmp = row.op !== 'is';
  const mins = Number(row.value) || 0;
  const [unit, setUnit] = useState(mins >= 60 && mins % 60 === 0 ? 'h' : 'm');
  const shown = unit === 'h' ? Math.round((mins / 60) * 10) / 10 : mins;
  return (
    <>
      <select aria-label={say('how long')} value={cmp ? `cmp:${row.op}` : `is:${row.value}`} onChange={(e) => {
        const [kind, v] = e.target.value.split(':');
        if (kind === 'is') set({ op: 'is', value: v });
        else set({ op: v, value: cmp ? row.value : '60' });
      }}>
        {DURATION_OPS.map(([k, w]) => <option key={k} value={`cmp:${k}`}>{w}</option>)}
        <option value="is:none">not set</option>
        <option value="is:any">set</option>
      </select>
      {cmp && (
        <span className="tdf-amount">
          <input type="number" min={unit === 'h' ? 0.5 : 5} max={unit === 'h' ? 24 : 1440} step={unit === 'h' ? 0.5 : 5}
                 aria-label={say(unit === 'h' ? 'hours' : 'minutes')} value={shown}
                 onChange={(e) => {
                   const k = Number(e.target.value);
                   if (!Number.isFinite(k) || k <= 0) return;
                   set({ value: String(Math.max(1, Math.min(1440, Math.round(unit === 'h' ? k * 60 : k)))) });
                 }} />
          <select aria-label={say('unit')} value={unit} onChange={(e) => setUnit(e.target.value)}>
            <option value="m">minutes</option>
            <option value="h">hours</option>
          </select>
        </span>
      )}
    </>
  );
}
